import type { CanvasElement, Point } from '@extapia/shared';
import { boundsOf, pointInBounds } from '@extapia/shared';

// Rotate point around element center inversely, so we can hit-test
// against un-rotated geometry.
const unrotate = (p: Point, el: CanvasElement): Point => {
  if (el.angle === 0) return p;
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  const c = Math.cos(-el.angle);
  const s = Math.sin(-el.angle);
  const dx = p.x - cx;
  const dy = p.y - cy;
  return { x: cx + dx * c - dy * s, y: cy + dx * s + dy * c };
};

const distanceToSegment = (p: Point, a: Point, b: Point): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const px = a.x + t * dx;
  const py = a.y + t * dy;
  return Math.hypot(p.x - px, p.y - py);
};

// Hit test a single element against a world-space point.
// Threshold (world units) controls how forgiving the hit is on strokes.
export const hitTest = (p: Point, el: CanvasElement, threshold = 8): boolean => {
  if (el.isDeleted || el.locked === true) return false;
  const q = unrotate(p, el);
  const bounds = boundsOf(el);

  switch (el.type) {
    case 'rect':
    case 'frame':
    case 'sticky':
    case 'image': {
      return pointInBounds(q, bounds);
    }
    case 'diamond': {
      // point-in-diamond via 4-half-plane test
      const midX = el.x + el.width / 2;
      const midY = el.y + el.height / 2;
      const rx = el.width / 2;
      const ry = el.height / 2;
      const nx = Math.abs(q.x - midX) / rx;
      const ny = Math.abs(q.y - midY) / ry;
      return nx + ny <= 1;
    }
    case 'ellipse': {
      const rx = el.width / 2;
      const ry = el.height / 2;
      if (rx <= 0 || ry <= 0) return false;
      const cx = el.x + rx;
      const cy = el.y + ry;
      const nx = (q.x - cx) / rx;
      const ny = (q.y - cy) / ry;
      return nx * nx + ny * ny <= 1;
    }
    case 'line':
    case 'arrow': {
      const pts = el.points;
      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i]!;
        const b = pts[i + 1]!;
        const dist = distanceToSegment(
          q,
          { x: el.x + a[0], y: el.y + a[1] },
          { x: el.x + b[0], y: el.y + b[1] },
        );
        if (dist <= threshold) return true;
      }
      return false;
    }
    case 'freehand': {
      const pts = el.points;
      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i]!;
        const b = pts[i + 1]!;
        const dist = distanceToSegment(
          q,
          { x: el.x + a[0], y: el.y + a[1] },
          { x: el.x + b[0], y: el.y + b[1] },
        );
        if (dist <= threshold) return true;
      }
      return false;
    }
    case 'text': {
      return pointInBounds(q, bounds);
    }
    default:
      return false;
  }
};

// Returns the topmost element (highest z-order) hit by point p, or null.
export const hitTestSelection = (
  p: Point,
  elements: CanvasElement[],
  threshold = 8,
): CanvasElement | null => {
  // Iterate reverse so top-most (last drawn) checked first
  const sorted = [...elements].sort((a, b) => a.index.localeCompare(b.index));
  for (let i = sorted.length - 1; i >= 0; i--) {
    const el = sorted[i]!;
    if (hitTest(p, el, threshold)) return el;
  }
  return null;
};
